import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { prepareForSave, toEntries, type LeadPhone } from '@/lib/leads/leadPhones';
import type { MultiValueEntry } from '@/types/fieldRegistry';

// Acesso a `public.lead_phones`. Toda ESCRITA passa por RPC, nunca por insert/update direto,
// por dois motivos (ver a migration 20260912110000_lead_phones.sql):
//   1. trocar o conjunto de telefones precisa ser atômico;
//   2. promover um número muda `leads.phone` e pode colidir com `unique_lead_per_org_idx` —
//      a RPC traduz esse 23505 em PHONE_CONFLICT, que dá para mostrar ao usuário.

const LEAD_PHONES_KEY = (leadId: string | undefined) => ['lead-phones', leadId] as const;

/** Erro de colisão de identidade do lead, tratável pela UI. */
export function isPhoneConflict(error: unknown): boolean {
  return String((error as { message?: string })?.message ?? '').includes('PHONE_CONFLICT');
}

function friendlyError(error: unknown): string {
  const raw = String((error as { message?: string })?.message ?? '');
  if (isPhoneConflict(raw ? { message: raw } : error)) {
    return 'Já existe outro lead nesta organização com o mesmo nome, e-mail e telefone. Escolha outro telefone principal ou una os leads.';
  }
  return raw || 'Não foi possível salvar os telefones.';
}

/**
 * Grava o conjunto de telefones de um lead que JÁ existe, fora do hook de propósito: quem cria
 * (`LeadCreate`, `LeadCreateModal`, `LinkLeadToDealModal`) só conhece o id depois do insert, e
 * `LeadEdit` não monta o hook.
 *
 * Devolve a mensagem de erro em vez de lançar. O lead já foi gravado e o trigger de espelho já
 * criou a linha do principal a partir de `leads.phone` — falhar aqui custa os SECUNDÁRIOS, não o
 * lead, então é aviso e não erro. Mesma decisão da Fase 4 no motor de importação.
 */
export async function persistLeadPhones(
  leadId: string,
  entries: MultiValueEntry[],
): Promise<{ error: string | null }> {
  const prepared = prepareForSave(entries);
  const { error } = await supabase.rpc('set_lead_phones', {
    p_lead_id: leadId,
    p_phones: prepared.map((e) => ({ value: e.value, label: e.label, is_primary: e.is_primary })),
  });
  return { error: error ? friendlyError(error) : null };
}

export function useLeadPhones(leadId: string | undefined) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const query = useQuery({
    queryKey: LEAD_PHONES_KEY(leadId),
    enabled: !!leadId,
    queryFn: async (): Promise<LeadPhone[]> => {
      const { data, error } = await supabase
        .from('lead_phones')
        .select('id, lead_id, phone, label, is_primary, is_whatsapp, source, created_at')
        .eq('lead_id', leadId!)
        .order('is_primary', { ascending: false })
        .order('created_at', { ascending: true });

      if (error) throw error;
      return (data ?? []) as LeadPhone[];
    },
  });

  // `leads.phone` é espelho do principal e é mantido por trigger, então qualquer escrita aqui
  // muda o lead também — daí invalidar as listas de leads junto.
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: LEAD_PHONES_KEY(leadId) });
    queryClient.invalidateQueries({ queryKey: ['leads'] });
    queryClient.invalidateQueries({ queryKey: ['leads-infinite'] });
    queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
  };

  const setPhones = useMutation({
    mutationFn: async (entries: MultiValueEntry[]) => {
      if (!leadId) throw new Error('Lead não identificado');
      // Aplica as mesmas regras do banco antes de mandar: dedupe canônico + um principal só.
      const prepared = prepareForSave(entries);
      const { data, error } = await supabase.rpc('set_lead_phones', {
        p_lead_id: leadId,
        p_phones: prepared.map((e) => ({
          value: e.value,
          label: e.label,
          is_primary: e.is_primary,
        })),
      });
      if (error) throw error;
      return data;
    },
    onSuccess: invalidate,
    onError: (error) =>
      toast({ variant: 'destructive', title: 'Falha ao salvar telefones', description: friendlyError(error) }),
  });

  const setPrimary = useMutation({
    mutationFn: async (phoneId: string) => {
      const { error } = await supabase.rpc('set_primary_lead_phone', { p_phone_id: phoneId });
      if (error) throw error;
    },
    onSuccess: invalidate,
    onError: (error) =>
      toast({ variant: 'destructive', title: 'Não foi possível definir o principal', description: friendlyError(error) }),
  });

  const removePhone = useMutation({
    mutationFn: async (phoneId: string) => {
      const { error } = await supabase.rpc('delete_lead_phone', { p_phone_id: phoneId });
      if (error) throw error;
    },
    onSuccess: invalidate,
    onError: (error) =>
      toast({ variant: 'destructive', title: 'Não foi possível remover o telefone', description: friendlyError(error) }),
  });

  const phones = query.data ?? [];

  return {
    phones,
    /** Pronto para alimentar o MultiValueRenderer. */
    entries: toEntries(phones),
    isLoading: query.isLoading,
    setPhones,
    setPrimary,
    removePhone,
  };
}
